import { t, translate } from '@avalara/bat-apps-shared/i18n';
import { SAlert, SCol, SRow } from '@avalara/skylab-sdk/react';

import { formatPrice } from '../../../../shared/helpers';
import { getSalestaxDisplayText } from '../../../reducers/helpers';
import Discount from '../order-summary/discount';
import OrderItem from '../order-summary/order-item';
import OrderReviewPriceTable from './price-table';
import OrderReviewQuantityRow from './quantity-row';

import type { TranslationKey } from '@avalara/bat-apps-shared/i18n';
import type { OfferItem } from '@avalara/types/offers';
import type { Currency } from '../../../../../types/currency';
import type { PriceTableRow, ProductPricingTier } from '../../../../../types/product-detail';

import './order-review.css';

type Props = {
  // Optional: the PDP tier that sells this offer might not be found (e.g. the reverse offerFlow
  // lookup finds nothing) — the summary still renders using productItems/total in that case, just
  // without the fine print extra.
  tier?: ProductPricingTier;
  productItems: Array<OfferItem>;
  additionalItems: Array<OfferItem>;
  currency?: Currency;
  discountAmount: number;
  discountName?: string | null;
  salesTax: number | null;
  // False for offers with no taxCode (e.g. Registrations, BRGUI-2581) — tax there is calculated at
  // invoicing rather than estimated at checkout, so this row would otherwise always read
  // "Calculated at checkout" regardless of the offer.
  showSalesTax: boolean;
  // Opt-in — see OfferOrderSummary.showTierFinePrint. Most offers don't set this, so `tier`'s fine
  // print stays PDP-only unless an offer explicitly asks for it here too.
  showTierFinePrint?: boolean;
  // Opt-in — see OfferOrderSummary.showInlineDiscount.
  showInlineDiscount?: boolean;
  total: number;
  adjustableQuantity?: boolean;
  quantity: number;
  preDiscountSubtotal: number;
  minQuantity?: number;
  maxQuantity?: number;
  priceTable?: Array<PriceTableRow>;
  // Per-return overage rate for the current quantity (BRGUI-2975) -- only set for offers that
  // publish overagePricingTiers (Managed Returns today); undefined/null hides the warning.
  overagePrice?: number | null;
  onChangeQuantity: (_: number) => void;
};

const OrderReviewSummary = ({
  tier,
  productItems,
  additionalItems,
  currency,
  discountAmount,
  discountName,
  salesTax,
  showSalesTax,
  showTierFinePrint,
  showInlineDiscount,
  total,
  adjustableQuantity,
  quantity,
  preDiscountSubtotal,
  minQuantity,
  maxQuantity,
  priceTable,
  overagePrice,
  onChangeQuantity,
}: Props) => {
  // Only takes effect for a single, non-adjustable-quantity item with no chargeable additional
  // items -- every other shape (multiple productItems, a volume-priced/adjustable-quantity offer,
  // or additionalItems that aren't waived and so still contribute to the order total, per
  // OfferItem.waived) keeps the standard separate Discount row below. A chargeable additional item
  // means discountAmount can't be attributed solely to the one product item without risking a
  // misleading item price/percentage badge, even though the final total stays correct either way.
  // Also requires a positive item price no smaller than the discount: a zero price would divide by
  // zero into an Infinity% badge, and a discount larger than the price would render a negative item
  // price and a >100% badge -- both fall back to the separate Discount row instead, which has no
  // such constraint.
  const hasChargeableAdditionalItems = additionalItems.some((item) => !item.waived && item.price > 0);
  const soleItem =
    !adjustableQuantity && !hasChargeableAdditionalItems && productItems.length === 1 ? productItems[0] : undefined;
  const inlineDiscountItem =
    showInlineDiscount && soleItem && discountAmount > 0 && soleItem.price > 0 && discountAmount <= soleItem.price
      ? soleItem
      : undefined;

  return (
    <div data-ref='buy.order-review.summary'>
      <SRow className='margin-bottom-md'>
        <SCol className='pad-bottom-none'>
          <h2 data-ref='buy.order-review.summary.heading'>{t('client.order-review.order-summary')}</h2>
        </SCol>
      </SRow>

      <SAlert
        status='info'
        noDismiss
        id='buy.order-review.summary.info-alert'
        className='product-detail-pricing-disclaimer margin-bottom-md show-icon'
      >
        {t(
          showSalesTax
            ? 'client.order-review.order-summary.info-alert'
            : 'client.order-review.order-summary.info-alert.no-sales-tax',
        )}
      </SAlert>

      {priceTable && priceTable.length > 0 && <OrderReviewPriceTable rows={priceTable} />}

      {adjustableQuantity && productItems[0] != null ? (
        <OrderReviewQuantityRow
          name={productItems[0].name}
          description={productItems[0].subtext}
          quantity={quantity}
          lineTotal={preDiscountSubtotal}
          currency={currency}
          minQuantity={minQuantity}
          maxQuantity={maxQuantity}
          strikethrough={discountAmount > 0}
          overagePrice={overagePrice}
          onChangeQuantity={onChangeQuantity}
        />
      ) : (
        productItems.map((item) => {
          const isInlineDiscountItem = item === inlineDiscountItem;
          const discountPercentageLabel = isInlineDiscountItem
            ? translate('client.order-review.summary.inline-discount-badge', {
                percentage: Math.round((discountAmount / item.price) * 100),
              })
            : undefined;

          return (
            <OrderItem
              key={item.name}
              name={item.name}
              price={formatPrice(isInlineDiscountItem ? item.price - discountAmount : item.price, 2, currency)}
              originalPrice={isInlineDiscountItem ? formatPrice(item.price, 2, currency) : undefined}
              badge={discountPercentageLabel}
              subtext={item.subtext}
              description={item.description}
            />
          );
        })
      )}

      {(additionalItems.length > 0 || showSalesTax || (discountAmount > 0 && !inlineDiscountItem)) && (
        <hr className='margin-all-xs' />
      )}

      {additionalItems.length > 0 && (
        <>
          {additionalItems.map((item) => (
            <OrderItem
              key={item.name}
              name={item.name}
              price={formatPrice(item.price, 2, currency)}
              strikethrough={item.waived}
              noPadBottom
              nameSuffix={
                item.waived && (
                  <span className='order-review-waived-badge' data-ref='buy.order-review.summary.waived-tag'>
                    {t((item.waivedLabelKey ?? 'client.order-review.waived-tag') as TranslationKey)}
                  </span>
                )
              }
            />
          ))}

          <hr className='margin-all-xs' />

          {(showSalesTax || (discountAmount > 0 && !inlineDiscountItem)) && <hr className='margin-all-xs' />}
        </>
      )}

      {showSalesTax && (
        <OrderItem
          name={t('client.order-review.sales-tax')}
          price={getSalestaxDisplayText(salesTax)}
          subtext={t('client.order-review.sales-tax.subtext')}
        />
      )}

      {discountAmount > 0 && !inlineDiscountItem && (
        <Discount currency={currency} discountAmount={discountAmount} discountName={discountName} subtotal={total} />
      )}

      {/* Only needed when the sales tax row or the discount row actually rendered above — otherwise
          this would sit directly against the additionalItems/product-items hr above with nothing
          between them, showing as a redundant blank divider line. */}
      {(showSalesTax || (discountAmount > 0 && !inlineDiscountItem)) && <hr className='margin-all-xs' />}

      <OrderItem
        name={t('client.order-review.total-due-today')}
        price={formatPrice(total, 2, currency)}
        nameStyle={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-normal)' }}
      />

      {showTierFinePrint && tier?.finePrint && (
        <SRow className='pad-top-xs'>
          <SCol>
            <p className='text-sm' data-ref='buy.order-review.summary.fine-print'>
              {tier.finePrint}
            </p>
          </SCol>
        </SRow>
      )}
    </div>
  );
};

export default OrderReviewSummary;
